import { connect } from 'react-redux';
import { PureComponent, ReactNode } from 'react';
import Modal from '../../components/common/Modal';
import ModalDialog from '../../components/common/ModalDialog';
import VitalsCardPadV2 from '../../components/prescriptionPad/rightPanel/workspace/padV2/VitalsCardPadV2';
import { AP } from '../Appointments';
import { Actions } from '../../store/actions';
import { GlobalState } from '../../store/reducers';
import { ThunkDispatch } from 'redux-thunk';
import {
    LabVitalEntity,
    PatientPersonalProfile,
    VitalsEntity,
} from '../../store/types/prescription';
import {
    cloneAndDelete,
    cloneAndUpsert,
    generatePrescriptionId,
    randomString,
} from '../../store/utils';
import pushPxDraft from '../../api/pushPxDraft';
import {
    getDoc,
    getGLocal,
    getItemFromMisc,
    getPatientByLocalId,
    MISC_KEYS,
} from '../../store/idbHandler';
import { captureErrorString, captureError } from '../captureError';
import { Button, notification } from 'antd';
import fetchPxDraft, { DraftResponse } from '../../api/fetchPxDraft';
import RightPopUp from '../../components/common/RightPopUp';
import { Data, LabVitalType, TableDataType } from '../../store/types/LabTests';
import {
    buildDateWiseLabVitalArray,
    calculateOutOfRange,
    calculateTotalFilledElements,
    filterAllFilledTableData,
    getAllLabTestsVitalKeys,
    getAllLabVitalKeysNotPresentInLabTest,
    tranformData,
    updateElementAndGetTableObj,
} from '../../components/prescriptionPad/rightPanel/workspace/labVitalsUtils';
import moment from 'moment';
import LabTestsTable from '../LabTestsTable';
import { LAB_VITAL_TESTS } from '../../labTestsConstants';
import { CalculatorsConfig, GenieSettings, VitalsConfig } from '../../api/pushGenie';
import { GVitals } from '../../store/db';
import { DB_TABLES } from '../../store/dbTables';
import {
    CUSTOM_VITALS_SAFE_RANGE,
    VITALS_WITHOUT_RANGE,
    changeVitalIdFromVToLBInVitalsConfig,
} from '../../components/prescriptionPad/leftPanel/vitals';
import { ONE_CHAR_GENDER } from '../NewPatient';
import { GROWTH_CHART_RESULTS } from '../../constants';

type Props = {
    appointment: AP;
    closeMe: () => void;
};

type State = {
    vitals: VitalsEntity[];
    apiState: 'idle' | 'loading' | 'success' | 'error';
    selectedTab: 'vitals' | 'lab-results';

    tableData: TableDataType[];
    dateColumns: (string | undefined)[];
    patient?: PatientPersonalProfile;
    labVitals?: LabVitalEntity[];
    calculatorsConfig?: CalculatorsConfig;
    vitalConfigData?: VitalsConfig;
    gVitalData: GVitals[];
};

class ApptVitalsAndLabResultsRightPopUp extends PureComponent<Props, State> {
    containerId = randomString(4);
    data = LAB_VITAL_TESTS as Data[];

    state: State = {
        vitals: [],
        apiState: 'idle',
        selectedTab: 'vitals',
        gVitalData: [],

        tableData: tranformData(this.data, this.data) || [],
        dateColumns: [moment().format('YYYY-MM-DD')],
    };

    componentDidMount = async (): Promise<void> => {
        const { appointment } = this.props;

        try {
            const [
                draftData,
                patientData,
                vitalsConfig,
                calculatorConfigs,
                gVitalsData,
            ] = await Promise.all([
                fetchPxDraft(appointment.aid, appointment.patient_oid || ''),
                getPatientByLocalId(appointment.patient_oid || ''),
                getItemFromMisc<VitalsConfig>(appointment.doctor_oid, MISC_KEYS.vitalsConfig),
                getItemFromMisc<CalculatorsConfig>(
                    appointment.doctor_oid,
                    MISC_KEYS.calculatorConfig,
                ),
                getGLocal<GVitals>(appointment.doctor_oid, DB_TABLES['vitals']),
            ]);

            if (draftData.success) {
                this.setState({
                    vitals: draftData?.response?.data?.tool?.medicalHistory?.vitals || [],
                    labVitals: draftData?.response?.data?.tool?.labVitals || [],
                    calculatorsConfig: calculatorConfigs,
                    vitalConfigData: changeVitalIdFromVToLBInVitalsConfig(vitalsConfig),
                    gVitalData: gVitalsData,
                });
            } else {
                throw new Error(
                    'Failed to fetch latest details of the rx' + JSON.stringify(draftData),
                );
            }

            this.loadData(draftData?.response?.data?.tool?.labVitals);
            this.setState({ patient: patientData?.personal as any });
        } catch (error) {
            this.loadData();

            captureError(error as Error);
            notification.error({
                message: 'Failed to fetch latest details of the rx',
                duration: 5,
            });
        }

        return;
    };

    onElementUpdate = (option: VitalsEntity): void => {
        this.setState({ vitals: cloneAndUpsert(option, this.state.vitals) });
        return;
    };

    onElementDelete = (id: string): void => {
        this.setState({ vitals: cloneAndDelete(id, this.state.vitals) });
        return;
    };

    onSubmit = async (): Promise<void> => {
        const { appointment, closeMe } = this.props;
        const { dateColumns, tableData, vitalConfigData } = this.state;

        this.setState({
            apiState: 'loading',
        });

        const finalPushVitals = this.state.vitals;

        let gender;

        if (appointment.patient_oid) {
            const patient = await getPatientByLocalId(appointment.patient_oid);

            if (patient) {
                gender = ONE_CHAR_GENDER[patient.personal.gender.toLowerCase()];
            }
        } else if (!gender && appointment.patient) {
            gender = appointment.patient.gen?.toLowerCase();
        }

        for (let i = 0; i < finalPushVitals.length; i += 1) {
            const vital = finalPushVitals[i];
            if (gender && CUSTOM_VITALS_SAFE_RANGE[vital.id]?.[gender]) {
                vital.value.safe = CUSTOM_VITALS_SAFE_RANGE[vital.id][gender];
            }
            const businessId = appointment.business_id || '';
            if (VITALS_WITHOUT_RANGE[businessId]?.has(vital.id)) {
                delete vital.value.safe;
            }
        }

        const map = finalPushVitals?.reduce((t, v) => {
            t[v.id] = v;
            return t;
        }, {} as { [k: string]: VitalsEntity });

        const orderedList: VitalsEntity[] =
            vitalConfigData?.selectedItems.map((t) => map[t.id]).filter(Boolean) || [];

        const restList: VitalsEntity[] =
            finalPushVitals.filter((t) => {
                return !orderedList.find((v) => v.id === t.id);
            }) || [];

        pushPxDraft(
            generatePrescriptionId(appointment.doctor_oid, appointment.aid),
            appointment.aid,
            {
                labVitals: filterAllFilledTableData(dateColumns, tableData),
                medicalHistory: {
                    vitals: [...orderedList, ...restList],
                },
            },
        )
            .then((r) => {
                if (r.success && r.draftId) {
                    notification.success({
                        message: 'Vitals & Lab Vitals submitted successfully',
                        duration: 5,
                    });
                    this.setState({
                        apiState: 'success',
                    });
                    closeMe();
                } else {
                    notification.error({
                        message: 'Error submitting vitals',
                        duration: 5,
                    });
                    this.setState({
                        apiState: 'error',
                    });
                }
            })
            .catch((e) => {
                notification.error({
                    message: 'Error submitting vitals',
                    duration: 5,
                });
                captureError(e);
                this.setState({
                    apiState: 'error',
                });
            });
    };

    setActiveTab = (types: 'vitals' | 'lab-results') => {
        this.setState({ selectedTab: types });
    };

    // lab results

    loadData = (labVitals?: LabVitalEntity[]) => {
        if (labVitals?.length) {
            const dateWiseLabVital = buildDateWiseLabVitalArray(labVitals);
            const dateArray = Object.values(dateWiseLabVital).map((value) => value.date);

            const tableData = tranformData(this.data, this.data, undefined, dateArray.length);

            for (let i = 0; i < dateArray.length; i += 1) {
                for (let j = 0; j < tableData.length; j += 1) {
                    for (let k = 0; k < tableData[j].labVitals.length; k += 1) {
                        const element =
                            dateWiseLabVital[i]?.data?.[tableData[j]?.labVitals?.[k]?.[i]?.id];

                        if (element) {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            tableData[j].labVitals[k][i] = {
                                ...tableData[j].labVitals[k][i],
                                ...element,
                            };
                        }
                    }
                }
            }
            this.setState({ tableData: tableData, dateColumns: dateArray });
        } else {
            this.setState({ tableData: tranformData(this.data, this.data) || [] });
        }
    };

    onSaveElementInLocalState = (labVitalObj: Partial<LabVitalType>, i: number) => {
        const { tableData } = this.state;
        this.setState({
            tableData: updateElementAndGetTableObj(tableData, labVitalObj, i),
        });
        return;
    };

    setDateColumns = (dateColumns: (string | undefined)[]) => {
        this.setState({ dateColumns: dateColumns });
    };

    setTableData = (tableData: TableDataType[]) => {
        this.setState({ tableData: tableData });
    };

    render(): ReactNode {
        const { closeMe } = this.props;
        const {
            vitals,
            apiState,
            selectedTab,
            tableData,
            dateColumns,
            labVitals,
            patient,
            calculatorsConfig,
            vitalConfigData,
            gVitalData,
        } = this.state;
        return (
            <RightPopUp closeMe={closeMe}>
                <>
                    <div className="bg-darwin-neutral-50 w-full pl-8 border-b border-neutral-8 select-none">
                        <div className="flex justify-between min-h-52 h-52 font-600 text-14 leading-28">
                            <div className="flex items-end ml-8 font-600 text-14 leading-28 cursor-pointer">
                                <div
                                    className={
                                        selectedTab === 'vitals'
                                            ? 'border-b-2 border-actionPrimary  font-700 w-120 flex h-full'
                                            : 'border-b-2 border-transparent font-700 w-120  flex h-full'
                                    }
                                    onClick={() => {
                                        this.setActiveTab('vitals');
                                    }}
                                >
                                    <p
                                        className={
                                            selectedTab === 'vitals'
                                                ? 'text-actionPrimary w-full h-full flex items-center justify-center text-actionPrimary text-16 font-700'
                                                : 'text-neutral-3 w-full h-full flex items-center justify-center text-actionPrimary text-16 font-700'
                                        }
                                    >
                                        Vitals
                                    </p>
                                </div>
                                <div
                                    className={
                                        selectedTab === 'lab-results'
                                            ? 'border-b-2 border-actionPrimary  font-700 w-120 flex h-full'
                                            : 'border-b-2 border-transparent font-700 w-120  flex h-full'
                                    }
                                    onClick={() => {
                                        this.setActiveTab('lab-results');
                                    }}
                                >
                                    <p
                                        className={
                                            selectedTab === 'lab-results'
                                                ? 'text-actionPrimary w-full h-full flex items-center justify-center text-actionPrimary text-16 font-700'
                                                : 'text-neutral-3 w-full h-full flex items-center justify-center text-actionPrimary text-16 font-700'
                                        }
                                    >
                                        Lab results
                                    </p>
                                </div>
                            </div>
                            {selectedTab === 'vitals' && (
                                <div className="flex bg-white px-16 py-9 my-8 rounded-8 text-14 items-center space-x-12 font-400">
                                    <div className="flex space-x-4 items-center">
                                        <div className="flex items-center space-x-8">
                                            <span className="font-700 text-darwin-accent-bright-yellow-900">
                                                {vitals.length || 0}
                                            </span>
                                            <span className="text-darwin-neutral-800">Filled</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {selectedTab === 'lab-results' && (
                                <div className="flex bg-white px-16 py-9 my-8 rounded-8 text-14 items-center space-x-24 font-400">
                                    <div className="flex space-x-4 items-center">
                                        <div className="flex items-center space-x-6">
                                            <span className="font-700 text-darwin-accent-bright-yellow-900">
                                                {calculateTotalFilledElements(
                                                    dateColumns,
                                                    tableData,
                                                )}
                                            </span>
                                            <span className="text-darwin-neutral-800">Filled</span>
                                        </div>
                                    </div>
                                    <div className="flex space-x-4 items-center">
                                        <div className="flex items-center space-x-6">
                                            <span className="font-700 text-darwin-status-warning-500">
                                                {calculateOutOfRange(dateColumns, tableData)}
                                            </span>
                                            <span className="text-darwin-neutral-800">
                                                Out of range
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="w-240"></div>
                        </div>
                    </div>
                    <div
                        id={this.containerId}
                        className="flex flex-col overflow-hidden h-full justify-between"
                    >
                        <div className="overflow-auto">
                            {selectedTab === 'vitals' && (
                                <VitalsCardPadV2
                                    icon={undefined}
                                    heading=""
                                    hideConfigure
                                    containerId={this.containerId}
                                    onElementDelete={this.onElementDelete}
                                    onElementUpdate={this.onElementUpdate}
                                    elements={vitals}
                                    hideGrowthChart
                                    vitalConfigData={vitalConfigData}
                                    calculatorsConfig={calculatorsConfig}
                                    gVitalData={gVitalData}
                                />
                            )}

                            {selectedTab === 'lab-results' && (
                                <div className="flex px-16">
                                    <LabTestsTable
                                        data={this.data}
                                        patient={patient}
                                        labVitals={labVitals}
                                        labDataNotPresentInTable={[]}
                                        tableData={tableData}
                                        dateColumns={dateColumns}
                                        setDateColumns={this.setDateColumns}
                                        onSaveElementInLocalState={this.onSaveElementInLocalState}
                                        setTableData={this.setTableData}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-between p-16 border-t border-neutral-7">
                            <div>
                                <button
                                    onClick={closeMe}
                                    className="neutral-btn  px-12 py-8 btn--small "
                                >
                                    <span className="w-full text-center">Cancel</span>
                                </button>
                            </div>
                            <div>
                                <Button
                                    type="primary"
                                    loading={apiState === 'loading'}
                                    onClick={this.onSubmit}
                                    size="large"
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                </>
            </RightPopUp>
        );
    }
}

export default ApptVitalsAndLabResultsRightPopUp;
